import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { FaArrowLeft, FaShareAlt, FaFacebook, FaTwitter, FaLinkedin, FaLink, FaCalendar, FaUser } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import { motion } from 'framer-motion';

const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [showShareAlert, setShowShareAlert] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchBlog = async () => {
      const blogDoc = doc(db, 'blogs', id);
      const blogSnapshot = await getDoc(blogDoc);

      if (blogSnapshot.exists()) {
        setBlog(blogSnapshot.data());
      }
    };

    fetchBlog();
  }, [id]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = blog?.title;
    
    let shareUrl;
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        setShowShareAlert(true);
        setTimeout(() => setShowShareAlert(false), 3000);
        return;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  if (!blog) {
    return (
      <div className="min-h-screen bg-perspective-carbon flex items-center justify-center">
        <div className="animate-pulse text-xl text-perspective-cloud/80">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-perspective-carbon text-perspective-sand relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute inset-0 bg-grid-soft [background-size:28px_28px] opacity-20" />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="h-16" />

        <main className="max-w-4xl mx-auto px-4 py-8">
          {/* Back Navigation */}
          <motion.button 
            onClick={handleGoBack}
            className="inline-flex items-center gap-2 text-perspective-cloud/80 hover:text-perspective-sage transition-colors mb-8 group"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
            Back
          </motion.button>

          {/* Article Header */}
          <motion.header 
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 text-sm text-perspective-cloud/70 mb-6 flex-wrap">
              <span className="bg-perspective-sage/15 text-perspective-sage px-3 py-1 rounded-full border border-perspective-sage/20">
                {blog.category}
              </span>
              <span className="text-perspective-cloud/50">•</span>
              <div className="flex items-center gap-2">
                <FaCalendar className="text-perspective-sage" />
                <time>{blog.date}</time>
              </div>
              <span className="text-perspective-cloud/50">•</span>
              <div className="flex items-center gap-2">
                <FaUser className="text-perspective-sage" />
                <span>{blog.author}</span>
              </div>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">{blog.title}</h1>
            
            {/* Share Options */}
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm text-perspective-cloud/70 flex items-center gap-2">
                <FaShareAlt className="text-perspective-sage" /> Share this article:
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleShare('facebook')}
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-perspective-sage hover:border-perspective-sage hover:text-perspective-carbon transition-all duration-300"
                  aria-label="Share on Facebook"
                >
                  <FaFacebook className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleShare('twitter')}
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-perspective-sage hover:border-perspective-sage hover:text-perspective-carbon transition-all duration-300"
                  aria-label="Share on Twitter"
                >
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleShare('linkedin')}
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-perspective-sage hover:border-perspective-sage hover:text-perspective-carbon transition-all duration-300"
                  aria-label="Share on LinkedIn"
                >
                  <FaLinkedin className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleShare('copy')}
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-perspective-sage hover:border-perspective-sage hover:text-perspective-carbon transition-all duration-300"
                  aria-label="Copy link"
                >
                  <FaLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.header>

          {/* Featured Image */}
          <motion.figure 
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-elevated">
              <img 
                src={blog.image} 
                alt={blog.title} 
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-perspective-carbon/40 to-transparent" />
            </div>
          </motion.figure>

          {/* Article Content */}
          <motion.article 
            className="prose prose-lg max-w-none mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div 
              className="blog-content text-perspective-cloud/90 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: blog.content }}
              style={{
                color: '#E7F1E4',
              }}
            />
            <style>{`
              .blog-content h1, .blog-content h2, .blog-content h3, .blog-content h4 {
                color: #FFFFFF;
                font-weight: 700;
                margin-top: 2rem;
                margin-bottom: 1rem;
              }
              .blog-content h1 { font-size: 2.5rem; }
              .blog-content h2 { font-size: 2rem; }
              .blog-content h3 { font-size: 1.5rem; }
              .blog-content p {
                margin-bottom: 1.5rem;
                line-height: 1.8;
              }
              .blog-content a {
                color: #B2E670;
                text-decoration: underline;
              }
              .blog-content a:hover {
                color: #C8F0A3;
              }
              .blog-content ul, .blog-content ol {
                margin-left: 1.5rem;
                margin-bottom: 1.5rem;
              }
              .blog-content li {
                margin-bottom: 0.5rem;
              }
              .blog-content code {
                background: rgba(255, 255, 255, 0.1);
                padding: 0.2rem 0.4rem;
                border-radius: 0.25rem;
                font-size: 0.9em;
              }
              .blog-content pre {
                background: rgba(255, 255, 255, 0.05);
                padding: 1rem;
                border-radius: 0.5rem;
                overflow-x: auto;
                margin-bottom: 1.5rem;
              }
              .blog-content blockquote {
                border-left: 4px solid #B2E670;
                padding-left: 1rem;
                margin-left: 0;
                margin-bottom: 1.5rem;
                font-style: italic;
                color: #C8F0A3;
              }
              .blog-content img {
                border-radius: 0.75rem;
                margin: 2rem 0;
                max-width: 100%;
                height: auto;
              }
            `}</style>
          </motion.article>

          {/* Related Links */}
          {blog.links?.length > 0 && (
            <motion.section 
              className="mt-12 p-6 bg-white/5 backdrop-blur border border-white/10 rounded-2xl shadow-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h3 className="text-xl font-semibold mb-4 text-white">Related Links</h3>
              <ul className="space-y-3">
                {blog.links.map((link, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <FaLink className="w-4 h-4 text-perspective-sage flex-shrink-0" />
                    <a 
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-perspective-sage hover:text-perspective-mint hover:underline transition-colors break-all"
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.section>
          )}
        </main>

        {/* Custom Share Alert */}
        {showShareAlert && (
          <motion.div 
            className="fixed bottom-4 right-4 bg-perspective-sage text-perspective-carbon px-6 py-3 rounded-xl shadow-elevated border border-perspective-sage/20 z-50"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center gap-2 font-semibold">
              <FaLink className="w-4 h-4" />
              Link copied to clipboard!
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BlogDetails;
